import { CommonModule } from '@angular/common';
import { CdkDrag, CdkDragDrop, CdkDropList, CdkDragHandle, CdkDragPlaceholder, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BaseComponent } from '@app/core/bases';
import { Photo, Product } from '@app/core/interfaces';
import { PhotoService, ProductService } from '@app/core/services';
import { ConfirmationService, PrimeIcons } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { OrderListModule } from 'primeng/orderlist';
import { TagModule } from 'primeng/tag';
import { takeUntil } from 'rxjs';
import { TabStates } from '../product-detail/product-detail';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ProductPhotosForm } from '../product-photos-form/product-photos-form';
import { DialogService } from 'primeng/dynamicdialog';
import Viewer from 'viewerjs';

@Component({
  selector: 'app-product-detail-photos',
  imports: [CommonModule, FormsModule, CardModule, TagModule, ButtonModule, ConfirmDialogModule, OrderListModule, CdkDropList, CdkDrag, CdkDragHandle, CdkDragPlaceholder, ProgressSpinnerModule],
  providers: [ConfirmationService, DialogService],
  templateUrl: './product-detail-photos.html'
})
export class ProductDetailPhotos extends BaseComponent implements OnChanges {
  @Input() product!: Product;
  @Output() productChange: EventEmitter<Product> = new EventEmitter<Product>();

  @Input() tabStates!: TabStates[];
  @Output() tabStatesChange: EventEmitter<TabStates[]> = new EventEmitter<TabStates[]>();
  
  @Input() tabIndex!: number;

  photos: Photo[] = [];

  viewerActiveIndex: number = 0;
  viewerInstance!: Viewer;

  isLoadingData: boolean = true;
  isSendingData: boolean = false;

  constructor(
    private dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private productService: ProductService,
    private photoService: PhotoService
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabStates']) {
      const tab: TabStates = this.tabStates.find(state => state.index === this.tabIndex) as TabStates
      if (tab.active && !tab.loaded) {
        this.getProductPhotos();
      }
    }
  }

  getProductPhotos(): void {
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.productService
      .getPhotoCollection(this.product.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.photos = data.results;
          this.product = { ...this.product, photos: this.photos };
          this.tabStates.map((state: TabStates) => {
            if (state.index === this.tabIndex)
              state.loaded = true
          });
        },
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.productChange.emit(this.product);
          this.isLoadingData = false;
        }
      });
  }
  
  showViewer(event: Event) {
    if (this.viewerInstance) {
      this.viewerInstance.destroy();
    }

    const gallery = document.createElement('div');
    this.product['photos']?.forEach((photo: Photo, index: number, photos: Photo[]) => {
      const img = new Image();
      img.src = this.apiUrl + photo.url;
      gallery.appendChild(img)
    })

    this.viewerInstance = new Viewer(gallery, {
      transition: false,
      ...this.viewerOptions,
      initialViewIndex: this.viewerActiveIndex
    });
    this.viewerInstance.show()
  }

  onDrop(event: CdkDragDrop<any[]>): void {
    this.isSendingData = true;

    moveItemInArray(this.photos, event.previousIndex, event.currentIndex);
    this.photos.map((photo: any, index: number) => {
      photo.ordre = index
    })

    const ordres: number[] = this.photos.map((photo) => photo.id) as number[];

    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.photoService
      .postOrder(ordres)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.productChange.emit(this.product);
          this.isSendingData = false;
        }
      });
  }

  showPhotoModal(): void {
    this.dialogRef = this.dialogService.open(ProductPhotosForm, {
      inputValues: {product: this.product},
      width: '600px',
      modal: true,
      focusOnShow: false,
      closable: true,
      styleClass: 'modal-no-content-padding',
      header: `Ajouter des photos`
    });

    this.dialogRef?.onClose.subscribe((data: Photo[] | null) => {
      this.getProductPhotos();
    });
  }

  deletePhoto(photo: Photo): void {
    this.confirmationService.confirm({
      message: 'Êtes-vous sûr de vouloir supprimer cette photo?',
      header: 'Confirmer la suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: PrimeIcons.CHECK,
      acceptButtonProps: {
        iconPos: 'right'
      },
      rejectIcon: PrimeIcons.TIMES,
      rejectButtonProps: {
        text: true,
        severity: 'danger'
      },
      accept: () => {
        this.photoService
          .delete(photo.id as string | number)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              const photos = this.product['photos']?.filter((p: Photo) => p.id != photo.id) as Photo[] || []
              this.photos = photos;
              this.product = { ...this.product, photos: photos };
              this.productChange.emit(this.product)
            },
            complete: () => this.toastService.add({ severity: 'success', summary: 'Succès', detail: 'Photo supprimé' }),
          });
      }
    });
  }
}
